import { Component, ViewChild, Input, OnInit, OnDestroy, Output, EventEmitter, OnChanges } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatPaginator } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { SubscriptionTable } from '../../model/subscription-table.model';
import { SubscriptionTableService } from '../../services/subscription-table.service';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { CookieService } from '../../services/cookie.service';
import {
  createBulkActionOptions,
  filterSubscriptionTableByMode,
  computeSubscriptionChanges,
  getInitialEventsToEmit,
  buildBulkActionsMap,
  getBaseColumns,
} from '../../helpers/subscription-table.helper';

enum MODE {
  EDIT = 'edit',
  VIEW = 'view',
}

export interface SubscriptionObject {
  subscriptionId: string;
  entityId: string;
  entityType: string;
  name: string;
  sms: boolean;
  voice: boolean;
  email: boolean;
  push: boolean;
  offer: boolean;
  performance: boolean;
  coachingEmail: boolean;
  coachingVoice: boolean;
  coachingSms: boolean;
  coachingPush: boolean;
  paymentEmail: boolean;
  remindersEmail: boolean;
  remindersPush: boolean;
  alertRuleEmail: boolean;
}

interface ActionConfig {
  key: string;
  translationKey: string;
  valuePrefix: string;
  labelProperty?: string;
}

interface BulkActionOption {
  value: string;
  viewValue: string;
}

@Component({
  selector: 'app-subscription-table',
  templateUrl: './subscription-table.component.html',
  styleUrls: ['./subscription-table.component.scss', '../../shared.styles.scss'],
})
export class SubscriptionTableComponent implements OnInit, OnDestroy, OnChanges {
  displayedColumns: string[] = [];

  @Input() mode: string;
  previousSubscriptions: SubscriptionTable[] = [];
  @Input() userId: string;
  @Input() hasOnboardingRole: boolean;
  @Input() hasDRFinancialRole: boolean;
  hasOnlyDRFinancialRole = false;
  public dataSource: MatTableDataSource<SubscriptionTable> = new MatTableDataSource();
  private paginator: MatPaginator;
  tooltips: Record<string, string> = {};

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }
  setDataSourceAttributes() {
    this.dataSource.paginator = this.paginator;

    if (this.paginator) {
      this.filterSubscriptionTable('');
    }
  }
  totalRecords = 0;
  pageSize = 10;
  loading = false;

  destroySubject$: Subject<void> = new Subject();
  searchText = '';

  @Input() hasAnyPhoneField: boolean;
  @Input() hasAnyMobilePhoneField: boolean;
  @Input() hasEmailField: boolean;
  @Input() hasContactMethodPush: boolean;
  @Output() addSubscriptionEvent = new EventEmitter<string>();
  @Output() delSubscriptionEvent = new EventEmitter<string>();

  EDIT = 'edit';
  rxSubscriptions: Subscription[] = [];
  contactSubscriptions: SubscriptionTable[] = [];

  @ViewChild('matRefDispatch') matRefDispatch: MatSelect;
  @ViewChild('matRefText') matRefText: MatSelect;
  @ViewChild('matRefVoice') matRefVoice: MatSelect;
  @ViewChild('matRefEmail') matRefEmail: MatSelect;
  @ViewChild('matRefOffer') matRefOffer: MatSelect;
  @ViewChild('matRefCoachingText') matRefCoachingText: MatSelect;
  @ViewChild('matRefCoachingVoice') matRefCoachingVoice: MatSelect;
  @ViewChild('matRefCoachingEmail') matRefCoachingEmail: MatSelect;
  @ViewChild('matRefCoachingOffer') matRefCoachingOffer: MatSelect;
  @ViewChild('matRefPerformance') matRefPerformance: MatSelect;
  @ViewChild('matRefPaymentEmail') matRefPaymentEmail: MatSelect;
  @ViewChild('matRefPaymentPush') matRefPaymentPush: MatSelect;
  @ViewChild('matRefReminderEmail') matRefReminderEmail: MatSelect;
  @ViewChild('matRefReminderPush') matRefReminderPush: MatSelect;
  @ViewChild('matRefAlertRuleEmail') matRefAlertRuleEmail!: MatSelect;

  iconTooltip: MatIconRegistry;

  allComplete = false;

  private labels: Record<string, string> = {};

  bulkSubActions: BulkActionOption[] = [];
  bulkSubActionsText: BulkActionOption[] = [];
  bulkSubActionsVoice: BulkActionOption[] = [];
  bulkSubActionsEmail: BulkActionOption[] = [];
  bulkSubActionsPush: BulkActionOption[] = [];
  bulkSubActionsOffer: BulkActionOption[] = [];
  bulkSubActionsPerformance: BulkActionOption[] = [];
  bulkSubActionsCoachingText: BulkActionOption[] = [];
  bulkSubActionsCoachingVoice: BulkActionOption[] = [];
  bulkSubActionsCoachingEmail: BulkActionOption[] = [];
  bulkSubActionsCoachingPush: BulkActionOption[] = [];
  bulkSubActionsPaymentEmail: BulkActionOption[] = [];
  bulkSubActionsPaymentPush: BulkActionOption[] = [];
  bulkSubActionsReminderEmail: BulkActionOption[] = [];
  bulkSubActionsReminderPush: BulkActionOption[] = [];
  bulkSubActionsAlertRuleEmail: BulkActionOption[] = [];

  isFocused: boolean = false;

  ngOnChanges() {
    this.refreshDisplayedColumns();
  }

  private refreshDisplayedColumns(): void {
    this.displayedColumns = getBaseColumns(this.hasDRFinancialRole, this.hasOnlyDRFinancialRole);
  }

  updateAllComplete() {
    this.allComplete =
      this.contactSubscriptions != null &&
      this.contactSubscriptions.every((contactSubscription) => contactSubscription.completed);
  }

  someComplete(): boolean {
    if (this.contactSubscriptions == null) {
      return false;
    }
    return (
      this.contactSubscriptions.filter((contactSubscription) => contactSubscription.completed).length > 0 &&
      !this.allComplete
    );
  }

  setAll(completed: boolean) {
    this.allComplete = completed;
    if (this.contactSubscriptions == null) {
      return;
    }
    this.contactSubscriptions.forEach((contactSubscription) => (contactSubscription.completed = completed));
    if (this.contactSubscriptions.filter((contactSubscription) => contactSubscription.completed).length == 0) {
      this.clearAllCheckboxSelections();
    }
  }

  toggleSiteCompleted(_site: SubscriptionTable) {}

  get isEditMode() {
    return this.mode === MODE.EDIT;
  }

  get isViewMode() {
    return this.mode === MODE.VIEW;
  }

  get shouldShowNaConsentMessage() {
    return (this.cookieService.getLocale() || 'en_US') === 'en_US';
  }

  get shouldShowNoPushContactMethodBanner() {
    const locale = this.cookieService.getLocale() || 'en_US';
    return this.isEditMode && !this.hasContactMethodPush && !['en_US', 'en_CA'].includes(locale);
  }

  constructor(
    private translateService: TranslateService,
    private subscriptionTableService: SubscriptionTableService,
    private cookieService: CookieService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
  ) {
    this.setupIcons();
  }

  private setupIcons(): void {
    this.iconTooltip = this.iconRegistry.addSvgIcon(
      'general-info',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/svg/general-info.svg'),
    );
  }

  private initializeLabels(): void {
    this.labels.assign = this.translateService.instant('user.table.bulk_sub_actions_label_assign');
    this.labels.unassign = this.translateService.instant('user.table.bulk_sub_actions_label_unassign');
    this.labels.typeAssign = this.translateService.instant('user.table.bulk_action_type_label_assign');
    this.labels.typeUnassign = this.translateService.instant('user.table.bulk_action_type_label_unassign');
    this.labels.paymentPush = this.translateService.instant('user.table.bulk_action_type_payment_push');
  }

  private initializeTooltips(): void {
    this.tooltips.push = this.translateService.instant('user.table.no_push_contact_method');
    this.tooltips.missing_onboarding_role = this.translateService.instant('user.table.missing_onboarding_role');
  }

  private setupBulkActions(): void {
    this.bulkSubActions = createBulkActionOptions('all', 'bulk-action', this.labels.assign, this.labels.unassign);

    const map = buildBulkActionsMap(this.labels as any);
    Object.keys(map).forEach((key) => ((this as any)[key] = map[key]));
  }

  ngOnInit(): void {
    this.translateService
      .get('user.table.bulk_sub_actions_label_assign')
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => {
        this.initializeLabels();
        this.initializeTooltips();
        this.setupBulkActions();
      });

    this.translateService.onLangChange.pipe(takeUntil(this.destroySubject$)).subscribe(() => {
      this.initializeLabels();
      this.initializeTooltips();
      this.setupBulkActions();
    });

    const loadingSubs = this.subscriptionTableService.loadingData$.subscribe((loading) => (this.loading = loading));

    const subscriptionTableData = this.subscriptionTableService.subscriptionTableData$.subscribe((tableData) => {
      this.setupTableDataSource(tableData);
    });

    const hasOnlyDRFinancialRoleSub = this.subscriptionTableService.hasOnlyDRFinancialRole$.subscribe(
      (hasOnlyDRFinancialRole) => {
        this.hasOnlyDRFinancialRole = hasOnlyDRFinancialRole;
        this.refreshDisplayedColumns();
      },
    );

    this.rxSubscriptions.push(...[loadingSubs, subscriptionTableData, hasOnlyDRFinancialRoleSub]);
  }

  private setupTableDataSource(sortedSubscriptionTable: SubscriptionTable[]): void {
    let filteredSubscriptions: SubscriptionTable[];

    if (this.isViewMode) {
      filteredSubscriptions = filterSubscriptionTableByMode(sortedSubscriptionTable);
    } else if (this.isEditMode) {
      filteredSubscriptions = sortedSubscriptionTable;
    }

    this.dataSource = new MatTableDataSource<SubscriptionTable>(filteredSubscriptions);
    this.totalRecords = this.dataSource.data.length;
    this.contactSubscriptions = this.dataSource.data as SubscriptionTable[];
    this.dataSource.paginator = this.paginator;

    getInitialEventsToEmit(filteredSubscriptions).forEach((event) => this.addSubscriptionEvent.emit(event));

    this.previousSubscriptions = filteredSubscriptions.map((subscription) => ({ ...subscription }));

    if (!this.isViewMode) {
      const previousServiceData = this.subscriptionTableService.previousSubscriptions;
      previousServiceData.forEach((previousSubscription) => {
        const currentSubscription = sortedSubscriptionTable.find(
          (sub) => sub.entityId === previousSubscription.entityId,
        );
        if (currentSubscription) {
          currentSubscription.paymentEmail =
            (!currentSubscription.paymentEmailSubscriptionId && previousSubscription.paymentEmail) ||
            currentSubscription.paymentEmail;
          currentSubscription.remindersEmail =
            (!currentSubscription.remindersEmailSubscriptionId && previousSubscription.remindersEmail) ||
            currentSubscription.remindersEmail;
          currentSubscription.alertRuleEmail =
            (!currentSubscription.alertRuleEmailSubscriptionId && previousSubscription.alertRuleEmail) ||
            currentSubscription.alertRuleEmail;
        }
      });
    }

    this.verifySubscriptionChanges();
  }

  filterSubscriptionTable(search: string): void {
    this.dataSource.filter = search.trim().toLowerCase();
  }

  changeBulkSubAction(type: string, action: { value?: string }) {
    this.clearAllCheckboxSelections();
    this.contactSubscriptions
      .filter((sub) => sub.completed)
      .forEach((sub) => {
        if (action.value?.includes('assign')) {
          (sub as any)[type] = true;
        }
        if (action.value?.includes('unassign')) {
          (sub as any)[type] = false;
        }
      });
    this.verifySubscriptionChanges();
  }

  onPaginateChange(event: any): void {
    if (event.pageIndex == Math.ceil(this.totalRecords / this.pageSize) - 1) {
      const data = this.dataSource.data;
      this.dataSource = new MatTableDataSource(data);
      this.totalRecords = this.dataSource.data.length;
      this.dataSource.paginator = this.paginator;
    }
  }

  changeBulkSubActions(action: { value?: string }) {
    this.changeBulkSubAction('sms', action);
    this.changeBulkSubAction('voice', action);
    this.changeBulkSubAction('email', action);
    this.changeBulkSubAction('push', action);
  }

  isSelectEnabled() {
    return this.contactSubscriptions.filter((sub) => sub.completed).length > 0;
  }

  clearAllCheckboxSelections() {
    this.matRefDispatch?.options.forEach((data: MatOption) => data.deselect());
    this.matRefText?.options.forEach((data: MatOption) => data.deselect());
    this.matRefVoice?.options.forEach((data: MatOption) => data.deselect());
    this.matRefEmail?.options.forEach((data: MatOption) => data.deselect());
  }

  handleSubscriptionCheckboxChange(subscriptionType: string, change: { checked: boolean }) {
    if (change.checked) {
      this.addSubscriptionEvent.emit(subscriptionType);
    } else {
      const subscriptionGroupMap: Record<string, string[]> = {
        sms: ['sms', 'coachingSms'],
        voice: ['voice', 'coachingVoice'],
        email: ['email', 'coachingEmail'],
      };

      const baseSubscriptionType = subscriptionType.replace('coaching', '').toLowerCase();
      const relatedSubscriptionTypes = subscriptionGroupMap[baseSubscriptionType] || [subscriptionType];

      const hasAnyActiveSubscription = relatedSubscriptionTypes.some((type) =>
        (this.dataSource.data as SubscriptionTable[]).some((subscriptionRow) => (subscriptionRow as any)[type]),
      );

      if (!hasAnyActiveSubscription) {
        this.delSubscriptionEvent.emit(baseSubscriptionType);
      }
    }

    this.verifySubscriptionChanges();
  }

  addSubscriptionForRole(roleName: string) {
    const previousCompletionStates: boolean[] = [];

    this.contactSubscriptions.forEach((subscription, index) => {
      previousCompletionStates[index] = subscription.completed;
      subscription.completed = true;
    });

    this.changeBulkSubAction(roleName + 'Email', { value: 'assign' });
    this.changeBulkSubAction(roleName + 'Push', { value: 'assign' });

    this.contactSubscriptions.forEach((subscription, index) => {
      subscription.completed = previousCompletionStates[index];
    });

    this.verifySubscriptionChanges();
  }

  verifySubscriptionChanges() {
    const changes = computeSubscriptionChanges(this.dataSource.data as SubscriptionTable[], this.previousSubscriptions);
    this.subscriptionTableService.hasChanges$.next(changes.length > 0);
  }

  onSearchInputFocus() {
    this.isFocused = true;
  }

  onSearchInputBlur() {
    this.isFocused = false;
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
    this.rxSubscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
